import { Link } from 'react-router-dom';

const policySections = [
  {
    title: 'Information We Collect',
    body: 'We collect information you provide when you create or update an artist or collaborator profile, including name, email address, optional mobile number, city, category, art form, bio, native-language bio, profile image, portfolio links, uploaded or linked media, enquiries, private messages, project posts, audition posts, competition participation, and admin-support requests.',
  },
  {
    title: 'How We Use Information',
    body: 'We use information to create accounts, verify access, display approved public profiles, match artists with discovery filters, manage enquiries and PMB conversations, review media, run Projects, Competitions, Live Auditions, AI Studio, notifications, support, safety moderation, platform analytics, and respond to legal or security requests.',
  },
  {
    title: 'Consent and Notice',
    body: 'When a user creates an account, accepts platform policies, submits profile details, uploads media, sends enquiries, joins platform tools, or continues using Kalamanch.art, the user authorizes Tivaro LLP to process the information reasonably required to provide, secure, moderate, improve, and administer the platform. Users should not submit personal information of another person unless they have lawful authority or consent to do so.',
  },
  {
    title: 'Public Profile Visibility',
    body: 'Approved artist and collaborator profiles may be visible to visitors. Contact details and portfolio links may be shown according to platform privacy rules, login status, and the information a user chooses to provide.',
  },
  {
    title: 'Images, Videos, and Portfolio Links',
    body: 'Profile images, project images, YouTube links, audition links, Instagram links, Spotify links, and other portfolio details may be reviewed by admin before being displayed. Media that appears unsafe, adult, illegal, misleading, infringing, or unrelated may be removed or restricted.',
  },
  {
    title: 'Messages and Enquiries',
    body: 'Enquiries and PMB messages are used to support communication between artists, collaborators, and the platform admin team. Admin may review message threads for safety, abuse prevention, support, and dispute handling.',
  },
  {
    title: 'Independent Dealings and Payments',
    body: 'Kalamanch.art may help users discover and communicate with each other, but independent collaborations, bookings, classes, workshops, enrollments, meetings, contracts, payments, refunds, and offline dealings happen between the concerned users. Tivaro LLP does not require users to share sensitive financial data on PMB and is not responsible for user-to-user payment decisions made outside official platform payment features.',
  },
  {
    title: 'Projects, Competitions, and Live Auditions',
    body: 'Project posts, artist responses, competition entries, audition posts, invited artist status, selected participants, and YouTube live links may be stored and reviewed. Public display depends on admin approval and feature-specific settings. If a collaborator uses their own YouTube Live URL, the live stream is hosted on that collaborator channel, but the listing can still be reviewed on Kalamanch.art.',
  },
  {
    title: 'AI Studio and Moderation Logs',
    body: 'AI Studio inputs and generated outputs may be processed through AI providers and may be logged for usage limits, abuse prevention, safety review, and admin moderation. Do not submit private, illegal, abusive, sexual, hateful, impersonation, or unsafe content to AI Studio.',
  },
  {
    title: 'Location, Near Me, and Discovery',
    body: 'The Android app may request approximate or precise location only for Near Me artist discovery. If location permission is denied, Kalamanch may use city or profile location instead. Users can control location permission from their device settings.',
  },
  {
    title: 'Push Notifications and Device Tokens',
    body: 'If enabled, the Android app may collect a Firebase device token and notification preference to send PMB, project, audition, competition, account, and admin alerts. Users can disable notifications in the app or device settings.',
  },
  {
    title: 'Third-Party Services',
    body: 'Kalamanch.art may use third-party services for hosting, database storage, Firebase authentication, push notifications, analytics, CAPTCHA, image/file hosting, email, OTP, AI generation, malware or safety scanning, embedded media, maps, event search, and YouTube live integrations. These services may process information only as needed to operate, protect, and improve the platform.',
  },
  {
    title: 'Data Security',
    body: 'We use reasonable technical and administrative safeguards, but no online service can guarantee absolute security. Users should protect their passwords, devices, and account access.',
  },
  {
    title: 'Your Choices',
    body: 'You may request profile correction, media removal, notification changes, account support, account deletion, or privacy assistance by contacting Kalamanch.art. Some information may need to be retained where required for security, legal, fraud-prevention, moderation, dispute, or platform integrity reasons.',
  },
  {
    title: 'Grievance, Correction, and Withdrawal Requests',
    body: 'Subject to applicable law and platform security requirements, users may request access support, correction, deletion, withdrawal of consent for optional processing, or grievance review by contacting Kalamanch.art. Withdrawal or deletion requests may limit account access, public visibility, PMB history, verification, safety review, or other platform features where the information is needed to operate the service or comply with law.',
  },
  {
    title: 'Children and Guardian Consent',
    body: 'Kalamanch.art is intended for lawful professional and creative use. Minors should use the platform only with parent or lawful guardian involvement, and users must not upload personal information, images, or contact details of a child without proper authority and consent.',
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50 pt-24" data-testid="privacy-policy-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00] mb-4">Launch policy</p>
          <h1 className="text-4xl sm:text-5xl font-bold gradient-text mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective date: July 7, 2026</p>

          <div className="rounded-2xl bg-orange-50 p-5 text-gray-700 leading-7 mb-10">
            Kalamanch.art is a creative discovery and connection platform owned and operated by Tivaro LLP. This
            policy explains how Tivaro LLP handles artist, collaborator, profile, media, enquiry, project, audition,
            AI Studio, notification, and account information through Kalamanch.art.
          </div>

          <div className="space-y-8 text-gray-700 leading-8">
            {policySections.map((section) => (
              <section key={section.title}>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p>{section.body}</p>
              </section>
            ))}

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Platform Owner and Operator</h2>
              <p>
                Kalamanch.art is owned and operated by <span className="font-semibold">Tivaro LLP</span>. References
                to "we", "us", or "our" in this Privacy Policy refer to Tivaro LLP in its operation of the
                Kalamanch.art platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Contact</h2>
              <p>
                For privacy-related questions, profile correction, or media removal requests, contact us at{' '}
                <span className="font-semibold">me@kalamanch.art</span>.
              </p>
            </section>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/terms-and-conditions" className="btn-primary text-center">
              Read Terms & Conditions
            </Link>
            <Link to="/contact" className="btn-outline text-center">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
